import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, type FormInstance, Radio, Spin, Tag } from '@arco-design/web-react';
import { get } from 'lodash';

import { EmptyTips, TagFilters } from '@/components';
import type { TagFilter } from '@/components/TagFilters/types';
import { formatVendorDeviceCellLabel } from '@/delivery-type-plugin/components/vendorAndDeviceRadioList';

import { getEbmResourceConfigurations } from '../api';
import styles from '../index.module.less';
import type { EbmResourceConfigPaths, EbmResourceConfigurationRecord } from '../types';
import {
  formatEbmCapacityText,
  formatEbmDeviceText,
  formatEbmMemoryText,
  formatEbmSpecListText,
} from '../utils/formatResourceConfigurationDisplay';

/** 汇总可搜索文本，供本地关键词过滤 */
function getSearchableText(item: EbmResourceConfigurationRecord): string {
  const parts: string[] = [];
  if (item.cpu?.device) parts.push(String(item.cpu.device));
  if (item.cpu?.vendor) parts.push(String(item.cpu.vendor));
  if (item.memory?.deviceType) parts.push(String(item.memory.deviceType));
  if (get(item, 'memory.resource.size')) parts.push(String(get(item, 'memory.resource.size')));
  if (item.gpu?.device) parts.push(String(item.gpu.device));
  (item.storage || []).forEach((s) => {
    if (s?.device) parts.push(String(s.device));
    const capacityText = formatEbmCapacityText(s?.capacity);
    if (capacityText) parts.push(capacityText);
  });
  (item.nic || []).forEach((n) => {
    if (n?.device) parts.push(String(n.device));
    if (n?.linkSpeed) parts.push(String(n.linkSpeed));
  });
  return parts.join(' ').toLowerCase();
}

function withDash(value: string): string {
  return value?.trim() ? value : '-';
}

/** 卡片主标题：优先 CPU，其次 GPU */
function getCardTitle(item: EbmResourceConfigurationRecord): string {
  if (item.cpu?.device) return formatVendorDeviceCellLabel(String(item.cpu.device));
  if (item.gpu?.device) return formatVendorDeviceCellLabel(String(item.gpu.device));
  return '-';
}

/** 卡片副标题：CPU 为主标题时用 GPU 补充说明 */
function getCardDescription(item: EbmResourceConfigurationRecord): string | undefined {
  if (item.cpu?.device && item.gpu?.device) {
    return formatVendorDeviceCellLabel(String(item.gpu.device));
  }
  return undefined;
}

interface ResourceCardProps {
  item: EbmResourceConfigurationRecord;
  selected: boolean;
  readOnly?: boolean;
  onSelect: () => void;
}

/** 资源配置卡片：布局对齐 ImageCard（Radio + 标题 + Tag） */
function ResourceCard({ item, selected, readOnly = false, onSelect }: ResourceCardProps) {
  const { t } = useTranslation();
  const nodeCount = item.nodes?.length ?? 0;
  const title = getCardTitle(item);
  const description = getCardDescription(item);

  const tags = [
    { label: 'CPU', value: withDash(formatEbmDeviceText(item.cpu)) },
    { label: t('common:memory'), value: withDash(formatEbmMemoryText(item.memory)) },
    { label: 'GPU', value: withDash(formatEbmDeviceText(item.gpu)) },
    { label: t('common:storage'), value: withDash(formatEbmSpecListText(item.storage)) },
    { label: t('data-center:network.card'), value: withDash(formatEbmSpecListText(item.nic)) },
  ];

  const handleClick = () => {
    if (readOnly || selected) return;
    onSelect();
  };

  return (
    <div
      className={`${styles.card} ${selected ? styles.selected : ''} ${readOnly ? styles.cardReadOnly : ''}`}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      role="button"
      tabIndex={readOnly ? -1 : 0}
      aria-pressed={selected}
      aria-readonly={readOnly || undefined}
    >
      <div className={styles.nodeBadge}>
        可用节点数
        <span className={styles.count}>{nodeCount}</span>
      </div>
      <div className={styles.cardRadio}>
        <Radio checked={selected} />
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {title}
          {description ? <span className={styles.cardDescription}>（{description}）</span> : null}
        </div>
        <div className={styles.cardTags}>
          {tags.map(({ label, value }, idx) => (
            <Tag key={idx} size="small" bordered>
              {label}：<span style={{ color: 'var(--color-text-3)' }}>{value}</span>
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
}

interface PhysicalResourceSelectorProps {
  form: FormInstance;
  paths: EbmResourceConfigPaths;
  /** 只读：不可改选 */
  readOnly?: boolean;
  /** 跳过列表拉取（计价模式隐藏时也应为 true） */
  skipListFetch?: boolean;
}

/**
 * 非计费物理资源配置选择。
 * 计价模式由外层 FormItem hidden + Input 承接 resourceConfiguration，本组件不挂载。
 */
export function PhysicalResourceSelector({ form, paths, readOnly = false, skipListFetch = false }: PhysicalResourceSelectorProps) {
  const { t } = useTranslation();
  const selectedValue = Form.useWatch(paths.resourceConfiguration, form) as string | undefined;
  const [data, setData] = useState<EbmResourceConfigurationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');

  const fetchData = useCallback(() => {
    if (skipListFetch) return;
    setLoading(true);
    getEbmResourceConfigurations()
      .then((res) => {
        if (Array.isArray(res)) {
          setData(res.map((item) => ({ ...item, key: JSON.stringify(item) })));
        }
      })
      .finally(() => setLoading(false));
  }, [skipListFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = useMemo(() => {
    if (!keyword.trim()) return data;
    const k = keyword.trim().toLowerCase();
    return data.filter((item) => getSearchableText(item).includes(k));
  }, [data, keyword]);

  const resourceSearchSchema: TagFilter[] = [{ key: 'keyword', label: t('common:search'), type: 'input', defaultSearch: true }];

  return (
    <>
      {!readOnly && !skipListFetch ? (
        <TagFilters
          placeholder={t('data-center:search.cpu.memory.gpu.storage.nic.or.vendor')}
          style={{ paddingTop: 8, paddingBottom: 0, backgroundColor: 'white' }}
          filters={resourceSearchSchema}
          resource={resourceSearchSchema}
          values={{ keyword }}
          onChange={(v) => {
            setKeyword((v.keyword as string) ?? '');
          }}
          onRefresh={fetchData}
        />
      ) : null}
      {!skipListFetch ? (
        <Spin loading={loading} block style={{ marginTop: readOnly ? 0 : 8 }}>
          <div className={styles.cardList}>
            {!loading && filteredData.length === 0 ? (
              <div className={styles.emptyWrap}>
                <EmptyTips />
              </div>
            ) : (
              filteredData.map((item) => (
                <ResourceCard
                  key={item.key}
                  item={item}
                  selected={selectedValue === item.key}
                  readOnly={readOnly}
                  onSelect={() => {
                    form.setFieldValue(paths.resourceConfiguration, item.key);
                  }}
                />
              ))
            )}
          </div>
        </Spin>
      ) : null}
    </>
  );
}
